<template>
  <div v-if="opened" class="modal-consulte-bg" :key="refreshCounter" :style="{ zIndex: zIndex }">
    <div
      v-if="opened"
      ref="dialogRef"
      class="modal fade show"
      tabindex="-1"
      role="dialog"
      :key="refreshCounter"
      :aria-labelledby="hideHeader ? undefined : idTitulo"
      :aria-label="hideHeader ? tituloAcessivel : undefined"
      aria-modal="true"
      style="display: unset; border-radius: var(--ct-modal-inner-border-radius)"
      @keydown.esc="close"
      @keydown="manterFocoDentro"
    >
      <div class="modal-dialog modal-dialog-centered" :style="{ width: modalWidth }">
        <div class="modal-content">
          <div v-if="!hideHeader" class="modal-header">
            <!--
              O `aria-labelledby` do diálogo aponta para ESTE id, gerado por instância.
              Antes era o literal "standard-modalLabel", que não existe em nenhuma das
              telas que usam o componente: a referência quebrada deixava o modal sem nome
              acessível, e o leitor de tela anunciava apenas "diálogo".

              `display: contents` (ver <style>) é o que permite embrulhar o slot sem mexer
              no layout: `.modal-header` é um contêiner flex, e um wrapper comum passaria a
              ser UM item de flex no lugar dos vários que o slot entrega hoje — o
              espaçamento entre ícone, título e botão de fechar mudaria nos ~30 modais que
              herdam daqui. Com `display: contents` os filhos do slot continuam sendo os
              itens de flex diretos, e o wrapper serve só de âncora para o id.
            -->
            <div :id="idTitulo" class="modal-consulte-titulo">
              <slot name="header">
                <h4 class="modal-title">Titulo do modal</h4>
              </slot>
            </div>
            <button
              type="button"
              v-if="!hideHeader"
              class="btn-close"
              @click="close"
              aria-label="Fechar"
              title="Fechar"
            ></button>
          </div>
          <div class="modal-body" :style="{ maxHeight: alturaMaximaCorpo }">
            <slot name="content" />
          </div>
          <div v-if="!hideFooter" class="modal-footer">
            <slot name="actions">
              <button type="button" class="btn btn-light" @click="close">Fechar</button>
            </slot>
          </div>
        </div>
        <!-- /.modal-content -->
      </div>
      <!-- /.modal-dialog -->
    </div>
    <!-- /.modal -->
  </div>
</template>

<script lang="ts">
  import {
    abrirDialogo,
    fecharDialogo,
    novoIdTitulo,
    prenderFoco,
  } from '@/uteis/dialogo-acessivel';
  import { defineComponent } from 'vue';

  export default defineComponent({
    name: 'ModalConsulte',
    props: {
      hideFooter: Boolean,
      hideHeader: {
        type: Boolean,
        default: false,
      },
      modalWidth: {
        type: String,
        default: '500px !important',
      },
      zIndex: {
        type: String,
        default: '1050 !important',
      },
      /**
       * Nome do modal para leitor de tela quando `hide-header` é usado — sem cabeçalho
       * não existe texto de onde tirar o nome. Só tem efeito com `hide-header`.
       */
      tituloAcessivel: {
        type: String,
        default: 'Janela de diálogo',
      },
    },
    emits: ['onClose'],
    computed: {
      /*
       * Altura útil do corpo do modal. Era fixa em `calc(100vh - 12rem)`, valor dimensionado para
       * modal COM cabeçalho e rodapé — e cabeçalho/rodapé ficam fora do modal-body, então quem
       * esconde os dois (`hide-header` + `hide-footer`, caso da tela de matricular estudante)
       * perdia ~8rem de altura sem ter nada ocupando esse espaço, e o conteúdo ganhava barra de
       * rolagem sem precisar. Agora só se reserva o que de fato é renderizado; as 4rem que sobram
       * são a folga do diálogo na tela.
       */
      alturaMaximaCorpo(): string {
        const reservadoEmRem = 4 + (this.hideHeader ? 0 : 4) + (this.hideFooter ? 0 : 5);

        return `calc(100vh - ${reservadoEmRem}rem)`;
      },
    },
    data() {
      return {
        opened: false,
        refreshCounter: 0,
        elementoAnterior: null as HTMLElement | null,
        idTitulo: novoIdTitulo('modal-consulte'),
      };
    },
    beforeUnmount() {
      // Um modal desmontado com `v-if` enquanto aberto (acontece ao trocar de rota com
      // o modal na tela) deixaria a rolagem do fundo travada para sempre.
      if (this.opened) {
        fecharDialogo(null);
      }
    },
    methods: {
      open() {
        this.refreshCounter += 1;
        this.opened = true;

        // O foco só pode ir para dentro depois de o diálogo existir no DOM.
        this.$nextTick(() => {
          this.elementoAnterior = abrirDialogo(this.$refs.dialogRef as HTMLElement | undefined);
        });
      },
      close() {
        this.refreshCounter += 1;
        this.opened = false;
        fecharDialogo(this.elementoAnterior);
        this.elementoAnterior = null;
        this.$emit('onClose');
      },
      /** Ver `prenderFoco` em src/uteis/dialogo-acessivel.ts. */
      manterFocoDentro(evento: KeyboardEvent) {
        prenderFoco(evento, this.$refs.dialogRef as HTMLElement | undefined);
      },
    },
  });
</script>
<style>
  .modal-consulte-bg {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1050;
    width: 100%;
    height: 100%;
    background-color: #24053d90;
  }

  /*
   * Âncora do `aria-labelledby` sem efeito de layout: os filhos do slot #header
   * continuam sendo itens de flex diretos do `.modal-header`. Ver o comentário no
   * template.
   */
  .modal-consulte-titulo {
    display: contents;
  }

  /*
   * Com modal aberto, a tela de trás não rola. Sem isto, rodar a roda do mouse sobre o
   * overlay rolava a página escondida atrás dele, e no celular o gesto de arrastar
   * dentro do modal "vazava" para o fundo.
   */
  body.modal-consulte-aberto {
    overflow: hidden;
  }

  /*
   * A largura vem por prop (ex.: "760px !important") e estouraria a tela em
   * celulares. O max-width vence o width, então limita sem quebrar o desktop.
   */
  .modal-consulte-bg .modal-dialog {
    max-width: calc(100vw - 1.5rem);
  }

  /*
   * SÓ o modal-body deste componente rola. O seletor era descendente
   * (`.modal-consulte-bg .modal-body`), então qualquer tela que montasse o próprio
   * `<div class="modal-content"><div class="modal-body">` dentro do slot #content — e são muitas —
   * ganhava um SEGUNDO contêiner com max-height + overflow, resultando nas duas barras de rolagem
   * (relatado na tela de matricular estudante, aberta pelo menu e pela enturmação).
   * O encadeamento de filho direto garante que o slot não seja atingido.
   */
  .modal-consulte-bg > .modal > .modal-dialog > .modal-content > .modal-body {
    /* A altura máxima vem do inline style (ver alturaMaximaCorpo); aqui fica só a rolagem, que
       continua sendo a rede de segurança para tela pequena/conteúdo longo. */
    overflow-y: auto;
  }
</style>
